import React, { useState, useCallback } from 'react';
import { useFetch } from '../../hooks/useFetch';
import api from '../../api/client';
import { Card, Alert, Btn, Spinner, EmptyState, Avatar, fmt, fmtDate } from '../../components/UI';

const BASE = window.location.origin;

export default function AdminReferrals() {
  const [success,   setSuccess]   = useState('');
  const [error,     setError]     = useState('');
  const [copied,    setCopied]    = useState(null);
  const [selected,  setSelected]  = useState(null); /* user whose referrals we're viewing */
  const [referred,  setReferred]  = useState(null);
  const [loadingRef, setLoadingRef] = useState(false);

  const fetchUsers = useCallback(() => api.get('/referrals'), []);
  const { data, loading, refetch } = useFetch(fetchUsers);
  const users = data?.users || [];

  async function generateLink(userId, userName) {
    setError(''); setSuccess('');
    try {
      const { data: res } = await api.post(`/referrals/generate/${userId}`);
      setSuccess(
        res.already_existed
          ? `${userName} already has a referral link.`
          : `Referral link generated for ${userName}!`
      );
      refetch();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate link.');
    }
  }

  function copyLink(code) {
    navigator.clipboard.writeText(`${BASE}/signup?ref=${code}`);
    setCopied(code);
    setTimeout(() => setCopied(null), 2500);
  }

  async function viewReferred(user) {
    setSelected(user);
    setLoadingRef(true);
    try {
      const { data: res } = await api.get(`/referrals/referred/${user.id}`);
      setReferred(res);
    } catch {
      setReferred({ referred: [], count: 0 });
    } finally {
      setLoadingRef(false);
    }
  }

  /* ── Referred users panel ── */
  if (selected) {
    return (
      <div style={{ padding:'28px 24px', fontFamily:"'DM Sans',sans-serif", maxWidth:860, margin:'0 auto' }}>
        <button onClick={() => { setSelected(null); setReferred(null); }} style={{ background:'none', border:'none', cursor:'pointer', color:'#4f6ef7', fontSize:14, fontWeight:600, marginBottom:20, display:'flex', alignItems:'center', gap:6, padding:0 }}>
          ← Back to all users
        </button>
        <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:24 }}>
          <Avatar name={selected.name} size={48} />
          <div>
            <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:20, fontWeight:700, color:'#1a1f5e', margin:0 }}>
              {selected.name}'s Referrals
            </h2>
            <p style={{ fontSize:13, color:'#9099b4', margin:0 }}>{selected.email}</p>
          </div>
        </div>

        {loadingRef ? <Spinner /> : (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
              <div style={{ background:'#eef1ff', borderRadius:12, padding:'16px 20px' }}>
                <div style={{ fontFamily:"'Sora',sans-serif", fontSize:32, fontWeight:700, color:'#4f6ef7' }}>{referred?.count || 0}</div>
                <div style={{ fontSize:13, color:'#636b89', marginTop:2 }}>Total Referred</div>
              </div>
              <div style={{ background:'#f0fdf4', borderRadius:12, padding:'16px 20px' }}>
                <div style={{ fontFamily:"'Sora',sans-serif", fontSize:32, fontWeight:700, color:'#16a34a' }}>
                  {fmt(referred?.referred?.reduce((s, u) => s + (u.total_earned||0), 0) || 0)}
                </div>
                <div style={{ fontSize:13, color:'#636b89', marginTop:2 }}>Total Earned by Referred</div>
              </div>
            </div>

            <Card title="Users Referred">
              {!referred?.referred?.length
                ? <EmptyState icon="👥" message="This user hasn't referred anyone yet." />
                : (
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse' }}>
                      <thead><tr>
                        {['Name','Email','Country','Total Earned','Joined','Status'].map(h => (
                          <th key={h} style={{ background:'#f8f9fc', padding:'10px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#9099b4', textTransform:'uppercase', letterSpacing:'.4px', borderBottom:'1px solid #e2e6f0', whiteSpace:'nowrap' }}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {referred.referred.map(u => (
                          <tr key={u.id} style={{ borderBottom:'1px solid #f0f2f8' }}>
                            <td style={{ padding:'12px 12px' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                <Avatar name={u.name} size={30} />
                                <span style={{ fontSize:14, fontWeight:600, color:'#1a1f5e' }}>{u.name}</span>
                              </div>
                            </td>
                            <td style={{ padding:'12px 12px', fontSize:13, color:'#636b89' }}>{u.email}</td>
                            <td style={{ padding:'12px 12px', fontSize:13, color:'#636b89' }}>{u.country||'—'}</td>
                            <td style={{ padding:'12px 12px' }}>
                              <strong style={{ color:'#16a34a', fontSize:14 }}>{fmt(u.total_earned)}</strong>
                            </td>
                            <td style={{ padding:'12px 12px', fontSize:12, color:'#9099b4' }}>{fmtDate(u.created_at)}</td>
                            <td style={{ padding:'12px 12px' }}>
                              <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20, background: u.is_active?'#dcfce7':'#fee2e2', color: u.is_active?'#16a34a':'#dc2626' }}>
                                {u.is_active ? 'Active' : 'Suspended'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              }
            </Card>
          </>
        )}
      </div>
    );
  }

  /* ── Main panel ── */
  return (
    <div style={{ padding:'28px 24px', fontFamily:"'DM Sans',sans-serif", maxWidth:900, margin:'0 auto' }}>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontFamily:"'Sora',sans-serif", fontSize:22, fontWeight:700, color:'#1a1f5e', margin:0 }}>Referral Tracking</h1>
        <p style={{ fontSize:14, color:'#9099b4', marginTop:4 }}>
          Generate a referral link for any user. Anyone who signs up through their link will be tracked under them.
        </p>
      </div>

      <Alert type="success" message={success} />
      <Alert type="error"   message={error} />

      <div style={{ background:'#eef1ff', borderRadius:12, padding:'14px 18px', marginBottom:20, fontSize:13, color:'#4338ca', lineHeight:1.6 }}>
        💡 <strong>How it works:</strong> Click <em>Generate Link</em> next to any user → copy their unique link → send it to whoever they want to invite → when someone signs up through that link, they appear under that user's referral count. Signup is still open to everyone — the link just tracks who brought them in.
      </div>

      <Card title={`All Users (${users.length})`}>
        {loading ? <Spinner /> : users.length === 0 ? (
          <EmptyState icon="👥" message="No users yet." />
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:640 }}>
              <thead><tr>
                {['User','Referral Link','Referrals','Joined','Actions'].map(h => (
                  <th key={h} style={{ background:'#f8f9fc', padding:'10px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#9099b4', textTransform:'uppercase', letterSpacing:'.4px', borderBottom:'1px solid #e2e6f0', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderBottom:'1px solid #f0f2f8' }}>

                    {/* User */}
                    <td style={{ padding:'13px 12px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <Avatar name={u.name} size={34} />
                        <div>
                          <div style={{ fontSize:14, fontWeight:600, color:'#1a1f5e' }}>{u.name}</div>
                          <div style={{ fontSize:12, color:'#9099b4' }}>{u.email}</div>
                        </div>
                      </div>
                    </td>

                    {/* Link */}
                    <td style={{ padding:'13px 12px' }}>
                      {u.code ? (
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <code style={{ fontSize:12, background:'#f0f4ff', color:'#4f6ef7', padding:'3px 8px', borderRadius:6, fontWeight:700, letterSpacing:1 }}>
                            {u.code}
                          </code>
                          <button onClick={() => copyLink(u.code)} style={{ padding:'4px 10px', background: copied===u.code?'#dcfce7':'#eef1ff', color: copied===u.code?'#16a34a':'#4f6ef7', border:'none', borderRadius:7, fontSize:11, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                            {copied===u.code ? '✅ Copied!' : '📋 Copy'}
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontSize:12, color:'#c8cedf', fontStyle:'italic' }}>No link yet</span>
                      )}
                    </td>

                    {/* Count */}
                    <td style={{ padding:'13px 12px' }}>
                      <span style={{ fontFamily:"'Sora',sans-serif", fontSize:18, fontWeight:700, color: u.referral_count>0?'#4f6ef7':'#c8cedf' }}>
                        {u.referral_count}
                      </span>
                    </td>

                    {/* Joined */}
                    <td style={{ padding:'13px 12px', fontSize:12, color:'#9099b4' }}>
                      {fmtDate(u.created_at)}
                    </td>

                    {/* Actions */}
                    <td style={{ padding:'13px 12px' }}>
                      <div style={{ display:'flex', gap:8 }}>
                        <button onClick={() => generateLink(u.id, u.name)} style={{ padding:'6px 12px', background:'#4f6ef7', color:'#fff', border:'none', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                          {u.code ? '🔄 Regenerate' : '+ Generate Link'}
                        </button>
                        {u.referral_count > 0 && (
                          <button onClick={() => viewReferred(u)} style={{ padding:'6px 12px', background:'#f0fdf4', color:'#16a34a', border:'1px solid #86efac', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                            👥 View {u.referral_count}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
